import {
  createMentorClassConversation,
  createMentorClassConversationInvitation,
  fetchMentorClassConversationCounts,
  fetchMentorClassConversations,
} from "classroom/api/teacher";
import type { ClassroomConversationInvitationFormData } from "conversations/types";
import {
  requireConversationContext,
  useConversationRowList,
} from "conversations/composables/useConversationRowList";
import { defineStore } from "pinia";

/**
 * The Gesprekken tab of a mentorklas, and of the "Alle mentorleerlingen" aggregate — the
 * `mentorClass` context carries `all` for the latter, and the API layer routes it.
 *
 * Every endpoint here is class-scoped, so each fetcher runs the context through
 * `requireConversationContext`: the composable also serves the teacher's own overview,
 * which has no class and hands its fetchers null.
 */
export const useClassroomMentorClassConversationStore = defineStore(
  "classroomMentorClassConversation",
  () => {
    const list = useConversationRowList({
      fetchList: (params, mentorClass) =>
        fetchMentorClassConversations(
          requireConversationContext(mentorClass),
          params,
        ),
      fetchCounts: (params, mentorClass) =>
        fetchMentorClassConversationCounts(
          requireConversationContext(mentorClass),
          params,
        ),
      createNote: (formData, mentorClass) =>
        createMentorClassConversation(
          requireConversationContext(mentorClass),
          formData,
        ),
    });

    /**
     * The classroom plan page's create. Not part of the shared list: the panel routes to
     * that page rather than calling this, and the teacher overview plans through the
     * standalone invitation endpoint instead.
     */
    const createInvitation = (
      formData: ClassroomConversationInvitationFormData,
    ) =>
      createMentorClassConversationInvitation(
        requireConversationContext(list.context.value),
        formData,
      );

    return { ...list, createInvitation };
  },
);
